import { Component, OnInit, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { GoogleAuthService, GoogleUser } from '../../../services/google-auth.service';
import { GoogleSigninComponent } from '../../google-signin/google-signin.component';
import { SettingsComponent } from '../../settings/settings.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, GoogleSigninComponent, RouterLink, SettingsComponent],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.css'
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  userName = 'User';
  isAdminPage = false;
  isLoggedIn = false;
  isInternalUser = false;
  isSettingsOpen = false;
  isSettingsDropdownOpen = false;
  private authSubscription: Subscription | null = null;
  private googleAuthSubscription: Subscription | null = null;

  @Output() logoutEvent = new EventEmitter<void>();
  @Output() loginEvent = new EventEmitter<void>();
  @Output() settingsEvent = new EventEmitter<void>();
  @Input() isAdmin: boolean = false;

  constructor(
    private authService: AuthService, 
    public googleAuthService: GoogleAuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    console.log('🏠 AppHeaderComponent: ngOnInit called - NEW AUTH ONLY');
    console.log('🏠 AppHeaderComponent: GoogleSigninComponent imported?', GoogleSigninComponent);
    console.log('🏠 AppHeaderComponent: GoogleAuthService injected?', this.googleAuthService);
    
    // Subscribe to auth state changes from the new Google auth service
    this.googleAuthSubscription = this.googleAuthService.authState$.subscribe(isLoggedIn => {
      console.log('🔄 Auth state changed (NEW Google service):', isLoggedIn);
      this.updateFromGoogleAuth();
    });

    // Subscribe to old auth service for backward compatibility
    this.authSubscription = this.authService.authState$.subscribe(isLoggedIn => {
      console.log('🔄 Auth state changed (old service - backward compatibility):', isLoggedIn);
      this.checkUserLoginStatus();
    });

    // Initial load
    this.updateFromGoogleAuth();
    this.checkUserLoginStatus();

    // Check if we're on the admin page
    this.isAdminPage = this.router.url.includes('/admin');
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.googleAuthSubscription) {
      this.googleAuthSubscription.unsubscribe();
    }
  }

  // Update component state from new Google Auth Service (PRIMARY)
  updateFromGoogleAuth(): void {
    const user = this.googleAuthService.getCurrentUser();
    const role = this.googleAuthService.getCurrentUserRole();
    
    if (user) {
      console.log('✅ User logged in via NEW Google Auth:', user.name);
      this.isLoggedIn = true;
      this.userName = user.name.length >= 25 ? user.given_name : user.name;
      this.isInternalUser = role === 'internal';
      console.log('📊 Updated state from Google Auth:', { 
        user: user.name, 
        role, 
        isInternal: this.isInternalUser 
      });
    } else {
      console.log('❌ No user found in NEW Google Auth');
    }
  }

  // Check old login status for backward compatibility
  checkUserLoginStatus(): void {
    const storedUser = localStorage.getItem('googleUser');
    
    if (storedUser && !this.googleAuthService.isLoggedIn()) {
      console.log('🔄 Found old localStorage user, migrating to new system...');
      const user = JSON.parse(storedUser);
      this.userName = user.name.length >= 25 ? user.name.split(' ')[0] : user.name;
      this.isLoggedIn = true;
      
      const userRole = localStorage.getItem('userRole');
      this.isInternalUser = userRole === 'internal' || 
                           (user.email && (user.email.endsWith('@mindmapsforupsc.com') || 
                                          user.email.endsWith('@admin.com')));
    }
  }

  // Handle user login event from new Google signin component
  onGoogleSignInSuccess(user: GoogleUser): void {
    console.log('🎉 Google sign-in success in header:', user);
    this.updateFromGoogleAuth();
    this.loginEvent.emit();
  }

  onGoogleSignInError(error: any): void {
    console.error('❌ Google sign-in error in header:', error);
  }

  onGoogleSignOutSuccess(): void {
    console.log('👋 Google sign-out success in header');
    this.isLoggedIn = false;
    this.userName = 'User';
    this.isInternalUser = false;
    this.logoutEvent.emit();
  }

  login() {
    console.log('🔗 Login clicked - redirecting to trigger new auth');
    this.loginEvent.emit();
  }

  logout() {
    console.log('🚪 Logout clicked');
    
    // Use new Google Auth Service logout (PRIMARY)
    if (this.googleAuthService.isLoggedIn()) {
      console.log('🔄 Logging out via NEW Google Auth Service');
      this.googleAuthService.logout();
    } else {
      console.log('🔄 Logging out via old auth service (fallback)');
      this.authService.logout();
    }
    
    this.isLoggedIn = false;
    this.userName = 'User';
    this.isInternalUser = false;
    this.isSettingsDropdownOpen = false;
    this.logoutEvent.emit();
  }

  // Handle settings button click
  openSettings() {
    console.log('⚙️ Settings clicked');
    this.isSettingsDropdownOpen = !this.isSettingsDropdownOpen;
    this.settingsEvent.emit();
  }
  
  // Navigate to settings page
  goToSettings() {
    this.isSettingsDropdownOpen = false;
    this.router.navigate(['/settings']);
  }
  
  // Handle close settings
  closeSettings() {
    this.isSettingsOpen = false;
  }
}
